import React, { useState } from "react";
import { createUserApi, getUserPublicKeyApi } from "../../../api/userApi.jsx";
import {
  importPublicKey,
  encryptData,
  bufferToBase64,
} from "../../../utils/cryptoUtils.js";

// UUID 생성을 위한 유틸리티
const generateUUID = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // fallback for older browsers
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export default function UserAdd({ onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    signInId: "",
    name: "",
    phone: "",
    email: "",
    password: "",
    passwordConfirm: "",
  });
  const [errorMessages, setErrorMessages] = useState({});
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrorMessages((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const validateForm = () => {
    let errors = {};

    // 아이디 검증
    if (
      !formData.signInId ||
      formData.signInId.length < 2 ||
      formData.signInId.length > 64
    ) {
      errors.signInId = "아이디는 2자 이상 64자 이하로 입력하세요.";
    }

    // 이름 검증
    if (
      !formData.name ||
      formData.name.length < 2 ||
      formData.name.length > 16
    ) {
      errors.name = "이름은 2자 이상 16자 이하로 입력하세요.";
    }

    // 연락처 검증
    const phoneRegex = /^(010|02|0\d{2})-(\d{3,4})-(\d{4})$/;
    if (!formData.phone || !phoneRegex.test(formData.phone)) {
      errors.phone = "유효한 연락처 형식이 아닙니다. 예: 010-1234-5678";
    }

    // 이메일 검증
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      errors.email = "유효한 이메일 주소를 입력하세요.";
    }

    // 비밀번호 검증
    if (
      !formData.password ||
      formData.password.length < 8 ||
      formData.password.length > 52
    ) {
      errors.password = "비밀번호는 8자 이상 52자 이하로 입력하세요.";
    }

    // 비밀번호 확인 검증
    if (formData.password !== formData.passwordConfirm) {
      errors.passwordConfirm = "비밀번호가 일치하지 않습니다.";
    }

    return errors;
  };

  const handleSave = async () => {
    const errors = validateForm();

    if (Object.keys(errors).length > 0) {
      setErrorMessages(errors);
      return;
    }

    setLoading(true);

    try {
      const keyId = generateUUID();
      const response = await getUserPublicKeyApi(keyId);

      if (response.success && response.data?.publicKey) {
        const publicKeyString = response.data.publicKey;
        const publicKey = await importPublicKey(publicKeyString);
        const encryptedPassword = await encryptData(
          publicKey,
          formData.password
        );
        const base64EncryptedPassword = bufferToBase64(encryptedPassword);

        const encryptedFormData = {
          ...formData,
          password: base64EncryptedPassword,
          keyId,
        };

        const apiResponse = await createUserApi(encryptedFormData);

        if (apiResponse.success) {
          if (onSuccess) onSuccess();
          if (onCancel) onCancel();
        } else {
          setErrorMessages({
            general: apiResponse.message || "관리자 추가에 실패했습니다.",
          });
        }
      } else {
        setErrorMessages({
          general: "공개키를 가져오는 중 오류가 발생했습니다.",
        });
      }
    } catch (error) {
      // console.error("API 호출 중 오류:", error);
      if (error.response && error.response.status === 409) {
        setErrorMessages({ signInId: "이미 존재하는 아이디입니다." });
      } else {
        setErrorMessages({ general: "관리자 추가 중 오류가 발생했습니다." });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="flex justify-between items-center border-b px-4 pt-3 pb-2">
        <h2 className="text-base font-semibold">관리자 추가</h2>
        <button
          onClick={onCancel}
          className="w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-black hover:bg-gray-200 focus:outline-none text-[10px] font-bold"
        >
          &#x2715;
        </button>
      </div>

      <div className="p-4">
        {errorMessages.general && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {errorMessages.general}
          </div>
        )}

        {/* 아이디 */}
        <div className="">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            아이디 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="signInId"
            value={formData.signInId}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errorMessages.signInId ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="아이디를 입력하세요"
          />
          {errorMessages.signInId && (
            <p className="text-red-500 text-xs mt-1">
              {errorMessages.signInId}
            </p>
          )}
        </div>

        {/* 이름 */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            이름 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errorMessages.name ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="이름을 입력하세요"
          />
          {errorMessages.name && (
            <p className="text-red-500 text-xs mt-1">{errorMessages.name}</p>
          )}
        </div>

        {/* 연락처 */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            연락처 <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errorMessages.phone ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="010-1234-5678"
          />
          {errorMessages.phone && (
            <p className="text-red-500 text-xs mt-1">{errorMessages.phone}</p>
          )}
        </div>

        {/* 이메일 */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            이메일 <span className="text-red-500">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errorMessages.email ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="email@example.com"
          />
          {errorMessages.email && (
            <p className="text-red-500 text-xs mt-1">{errorMessages.email}</p>
          )}
        </div>

        {/* 비밀번호 */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            비밀번호 <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            name="password"
            value={formData.password}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errorMessages.password ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="비밀번호를 입력하세요"
          />
          {errorMessages.password && (
            <p className="text-red-500 text-xs mt-1">
              {errorMessages.password}
            </p>
          )}
        </div>

        {/* 비밀번호 확인 */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            비밀번호 확인 <span className="text-red-500">*</span>
          </label>
          <input
            type="password"
            name="passwordConfirm"
            value={formData.passwordConfirm}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              errorMessages.passwordConfirm
                ? "border-red-500"
                : "border-gray-300"
            }`}
            placeholder="비밀번호를 다시 입력하세요"
          />
          {errorMessages.passwordConfirm && (
            <p className="text-red-500 text-xs mt-1">
              {errorMessages.passwordConfirm}
            </p>
          )}
        </div>
      </div>

      {/* 버튼 영역 */}
      <div className="border-t flex justify-end space-x-2 py-3 px-4">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          disabled={loading}
        >
          취소
        </button>
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "저장 중..." : "저장"}
        </button>
      </div>
    </div>
  );
}
